import React, { useEffect, useMemo, useState } from 'react'
import { Route } from 'react-router'
import {
  SmartList,
  IColumnElement,
  IRowElement,
  IHeaderItem,
  IconButton,
  SearchInput,
  PaginationPanel,
  Spinner,
  Container,
  Row,
  Icons
} from '@doseme/cohesive-ui'
import { TEAL, NAVY } from '@doseme/cohesive-ui'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IEntityHashMap, isLoading } from 'flux-entities'

import { TProps } from './types'
import { ShowContainer } from '../Show'
import { IPlan } from '../../../../types/plan'
import {
  plansEdit,
  plansShow,
  PLANS_CREATE_URL,
  PLANS_SHOW_URL
} from '../../../../constants/urls/frontend'
import { DISPLAY_ITEMS_PER_PAGE } from '../../../../constants/display'
import { paginate } from '../../../utils'
import { smartListData } from '../../utils'
import { alphabetical } from '../../../../utils/arrayUtils'
import { IPlansState } from '../../../../store/plans/types'

const cols: IHeaderItem[] = [
  {
    name: 'Plan Type',
  },
  {
    name: 'Name',
  },
  {
    name: 'Active Subs',
  },
  {
    name: 'Actions',
  }
]

const textContent = (plan: IPlan): string[] => [
  plan.type,
  plan.name,
]

// This method is memoized with useMemo() upon being called.
const getSearchableContent = (
  planIds: Array<number | string>,
  allPlans: IEntityHashMap<IPlan>
) => planIds.reduce<Record<number, string[]>>(
  (acc, x) => {
    return {
      ...acc,
      [x]: textContent(allPlans[x])
    }
  }, {}
)

const getPaginatedListWithPageCount = (
  plans: IPlansState,
  searchContent: Record<number, string[]>,
  searchText: string,
  columnContent: (plan: IPlan) => IColumnElement[],
  // @ts-ignore
  history: History<any>,
  currentPage: number,
  updateCurrentPage: (pages: number) => void
): { totalPages: number, paginatedData: IRowElement[] } => {
  let data: IRowElement[] = []

  if (!isLoading(plans)) {
    data = smartListData({
      ids: plans.ids.sort(
        (x, y) => {
          return alphabetical(
            plans.all[x].type,
            plans.all[y].type,
          )
        }
      ),
      all: plans.all,
      searchContent,
      searchText,
      columnizeRow: columnContent,
      getOnClickCallback: (entityId: number) => history.push(plansShow(entityId))
    })
  }

  const totalPages = Math.ceil(data.length / DISPLAY_ITEMS_PER_PAGE) || 1

  if (currentPage > totalPages) {
    updateCurrentPage(totalPages)
  }

  return {
    totalPages,
    paginatedData: paginate(data, { currentPage, itemsPerPage: DISPLAY_ITEMS_PER_PAGE })
  }
}

const Display: React.FC<TProps> = (props: TProps): JSX.Element | null => {
  const {
    plans,
    fetchPlans,
    deletePlan,
    history,
  } = props

  useEffect(() => {
    if (!plans.touched) { fetchPlans() }
  }, [plans.touched, fetchPlans])

  const [currentPage, updateCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')

  const searchableContent = useMemo(() => getSearchableContent(
    plans.ids,
    plans.all,
  ), [
    plans.ids,
    plans.all,
  ])

  if (!plans.touched) {
    return null
  }

  const confirmDelete = (plan: IPlan) => {
    if (window.confirm('Are you sure you want to delete this plan? This cannot be undone.')) {
      deletePlan(plan)
    }
  }

  const getDeleteButton = (plan: IPlan): JSX.Element | null => {
    if (plan.activeSubscriptions === 0) {
      return (
        <Icons.DeleteIconButton
          data-testid='delete-plan'
          disabled={plan.activeSubscriptions > 0}
          onClick={(e) => {
            e.stopPropagation()
            confirmDelete(plan)
          }}
        />
      )
    }

    return null
  }

  const columnContent = (plan: IPlan): IColumnElement[] => [
    {
      name: 'planType',
      element: (
        <span>
          {plan.type}
        </span>
      ),
      text: plan.type,
    },

    {
      name: 'name',
      element: (
        <span>
          {plan.name}
        </span>
      ),
      text: plan.name,
    },

    {
      name: 'activeSubscriptions',
      element: (
        <span>
          {plan.activeSubscriptions}
        </span>
      ),
      text: plan.activeSubscriptions?.toString(),
    },

    {
      name: 'actions',
      element: (
        <div className='d-flex align-items-center p-3'>
          <Icons.EditIconButton
            data-test='edit-button'
            color={TEAL}
            onClick={(e) => {
              e.stopPropagation()
              history.push(plansEdit(plan.id))
            }}
          />
          {getDeleteButton(plan)}
        </div>
      )
    }
  ]

  const { totalPages, paginatedData } = getPaginatedListWithPageCount(
    plans,
    searchableContent,
    searchText,
    columnContent,
    history,
    currentPage,
    updateCurrentPage
  )

  return (
    <div>
      <Container>
        <div className='d-flex justify-content-between my-2'>
          <div className='d-flex align-items-center'>
            <h4 className='co-h4 pr-2' data-test='plans-list'>Plans List</h4>
            {!isLoading(plans)
              ? <IconButton
                onClick={() => history.push(PLANS_CREATE_URL)}
                size='30px'
              >
                <FontAwesomeIcon icon={faPlus} />
              </IconButton>
              : <Spinner color={NAVY} size='lg' />}
          </div>
          <SearchInput
            value={searchText}
            onChange={setSearchText}
          />
        </div>
        <Row>
          <SmartList
            loading={isLoading(plans)}
            cols={cols}
            data={paginatedData}
            textIfEmpty={plans.ids.length ? 'No matching plans found.' : 'There are currently no plans.'}
          />
        </Row>
        <Row className='mt-2'>
          <PaginationPanel
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={updateCurrentPage}
          />
        </Row>
      </Container>
      <Route path={PLANS_SHOW_URL} component={ShowContainer} />
    </div>
  )
}

export {
  Display
}
